import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
  PROJECT_CREATE_STATUSES,
  PROJECT_STATUSES,
  PROJECT_FEE_PATTERN,
  PROJECT_VERSION_MAX,
  beauftragteLphGruppen,
  isProjectCreateStatus,
  isProjectStatus,
} from '@asas/domain/project-fields';
import type { ProjectStatus } from '@asas/domain/project-fields';
import {
  ARBEITSLISTE_STATUSES,
  STATUS_BEZEICHNUNG,
  STATUS_UEBERGAENGE,
} from '@asas/domain/project-status';
import type {
  LphGroup,
  Project,
  ProjectCompany,
  ProjectLeader,
  ProjectInput,
  ProjectUpdateInput,
} from '@asas/domain';
import { trpc } from './trpc';
import { klassifiziereFehler } from './daten/fehler';
import type { FehlerBefund } from './daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from './daten/serialisierung';
import { anmeldeUrlMitRueckkehr, erzeugeEntwurfsspeicher } from './daten/entwurf';
import { ArbeitspaketeBereich } from './arbeitspakete/ArbeitspaketeBereich';

export interface ProjektForm {
  id: string | null;
  version: number | null;
  number: string;
  name: string;
  projectManagerId: string;
  status: string;
  fee: string;
  /** Lead company (AC-201 extension, F4); '' = not chosen, the server may use the only one. */
  companyId: string;
  /** Commissioned phases (F1); sent on update only once changed, see `projektEingabe`. */
  beauftragteLeistungsphasen: number[];
  phasenGeaendert: boolean;
}
export const LEERES_PROJEKT: ProjektForm = {
  id: null,
  version: null,
  number: '',
  name: '',
  projectManagerId: '',
  status: 'akquise',
  fee: '',
  companyId: '',
  beauftragteLeistungsphasen: [],
  phasenGeaendert: false,
};
export function formularAusProjekt(p: Project): ProjektForm {
  return {
    id: p.id,
    version: p.version,
    number: p.number,
    name: p.name,
    projectManagerId: p.projectManagerId,
    status: p.status,
    fee: p.fee ?? '',
    companyId: p.companyId ?? '',
    beauftragteLeistungsphasen: [...p.beauftragteLeistungsphasen],
    phasenGeaendert: false,
  };
}
/** A tenant with exactly one company needs no choice (F4); the form shows it preselected. */
export function mitVorbelegterGesellschaft(
  form: ProjektForm,
  companies: readonly ProjectCompany[],
): ProjektForm {
  const [einzige] = companies;
  return form.companyId === '' && companies.length === 1 && einzige !== undefined
    ? { ...form, companyId: einzige.id }
    : form;
}
/**
 * Commissioned phases are chosen per whole LPH group (F1), stored as phases. Phases that
 * fit no whole group any more (after a regrouping) are dropped with the first change,
 * otherwise every save would be rejected (review PR #62, note 1).
 */
export function schalteLphGruppe(
  phasen: readonly number[],
  gruppen: readonly LphGroup[],
  gruppe: LphGroup,
  an: boolean,
): number[] {
  const ganze = gruppen
    .filter((g) => g.phases.every((phase) => phasen.includes(phase)))
    .flatMap((g) => g.phases);
  const ohne = ganze.filter((phase) => !gruppe.phases.includes(phase));
  return (an ? [...ohne, ...gruppe.phases] : ohne).sort((a, b) => a - b);
}
/** Display of commissioned phases; after a regrouping they may fit no group any more. */
export function lphAnzeige(phasen: readonly number[], gruppen: readonly LphGroup[]): string {
  if (phasen.length === 0) return 'nicht erfasst';
  const getroffen = beauftragteLphGruppen(phasen, gruppen);
  return getroffen === null
    ? `LPH ${phasen.join(', ')} (keine passende Gruppe)`
    : getroffen.map((g) => g.label).join(', ');
}
/** Input remains text; blank and zero have distinct meanings. */
export function projektEingabe(form: ProjektForm): ProjectInput | ProjectUpdateInput {
  const number = form.number.trim();
  const name = form.name.trim();
  const projectManagerId = form.projectManagerId.trim();
  const status = form.status;
  const fee = form.fee.trim() === '' ? null : form.fee.trim().replace(',', '.');
  if (
    number === '' ||
    name === '' ||
    projectManagerId === '' ||
    !isProjectStatus(status) ||
    (fee !== null && !PROJECT_FEE_PATTERN.test(fee))
  )
    throw new Error('Invalid project fields');
  const companyId = form.companyId.trim();
  const input = {
    number,
    name,
    projectManagerId,
    status,
    fee,
    // Omitted, the server uses the stored or the only company, or asks for a choice (F4).
    ...(companyId === '' ? {} : { companyId }),
  };
  if (form.id === null) {
    // AC-201 extension (F3): archiving is never part of creating a project.
    if (!isProjectCreateStatus(status)) throw new Error('Invalid project fields');
    return { ...input, status, beauftragteLeistungsphasen: form.beauftragteLeistungsphasen };
  }
  const version = form.version;
  if (
    version === null ||
    !Number.isInteger(version) ||
    version < 0 ||
    version > PROJECT_VERSION_MAX
  )
    throw new Error('Invalid project version');
  // Unchanged phases stay as stored, even if a regrouping no longer covers them (F1).
  const phasen = form.phasenGeaendert
    ? { beauftragteLeistungsphasen: form.beauftragteLeistungsphasen }
    : {};
  return { ...input, ...phasen, id: form.id, version };
}

export function pruefeProjektEntwurf(raw: unknown): ProjektForm | null {
  if (typeof raw !== 'object' || raw === null) return null;
  // Drafts from before the AC-201 extension lack the new fields; they get the defaults.
  const f = {
    companyId: '',
    beauftragteLeistungsphasen: [],
    phasenGeaendert: false,
    ...(raw as Partial<ProjektForm>),
  };
  if (
    (f.id !== null && typeof f.id !== 'string') ||
    (f.version !== null &&
      (!Number.isInteger(f.version) || typeof f.version !== 'number' || f.version < 0))
  )
    return null;
  if (
    [f.number, f.name, f.projectManagerId, f.status, f.fee, f.companyId].some(
      (v) => typeof v !== 'string',
    ) ||
    typeof f.phasenGeaendert !== 'boolean' ||
    !Array.isArray(f.beauftragteLeistungsphasen) ||
    !f.beauftragteLeistungsphasen.every((p) => Number.isInteger(p) && p >= 1 && p <= 9)
  )
    return null;
  return {
    id: f.id,
    version: f.version,
    number: f.number,
    name: f.name,
    projectManagerId: f.projectManagerId,
    status: f.status,
    fee: f.fee,
    companyId: f.companyId,
    beauftragteLeistungsphasen: f.beauftragteLeistungsphasen,
    phasenGeaendert: f.phasenGeaendert,
  } as ProjektForm;
}
export function projektFehlermeldung(befund: FehlerBefund, bearbeitung: boolean): string {
  if (befund.art === 'konflikt')
    return bearbeitung
      ? 'Das Projekt wurde zwischenzeitlich geändert oder die Projektnummer ist bereits vergeben. Ihre Änderung wurde nicht gespeichert. Bitte laden Sie den aktuellen Stand und prüfen Sie die Nummer.'
      : 'Die Projektnummer ist bereits vergeben. Bitte wählen Sie eine andere Nummer.';
  // The shared error layer gives no server message (AC-301 B8): name this action's causes.
  if (befund.art === 'eingabe')
    return 'Die Angaben wurden nicht gespeichert. Bitte prüfen Sie die Pflichtfelder, die federführende Gesellschaft und die LPH-Gruppen; laden Sie die Liste neu, falls sich Gesellschaften oder die LPH-Gruppierung inzwischen geändert haben.';
  const warning = befund.moeglicherweiseAusgefuehrt
    ? ' Der Vorgang könnte bereits gespeichert sein. Bitte prüfen Sie den Stand vor einem weiteren Versuch.'
    : '';
  return befund.meldung + warning;
}

/** The status control needs its own right (AC-202, E7); without it the action is not offered. */
export function darfStatusAendern(rechte: ReadonlySet<string>): boolean {
  return rechte.has('projects:change_status');
}
/** Without the status right a project is created as acquisition only (AC-202, E7). */
export function anlageStatuswerte(darfStatus: boolean): readonly ProjectStatus[] {
  return darfStatus ? PROJECT_CREATE_STATUSES : ['akquise'];
}
export function statusBezeichnung(status: string): string {
  return isProjectStatus(status) ? STATUS_BEZEICHNUNG[status] : status;
}
/** Allowed targets of a status change (AC-202, E3). */
export function statusZiele(status: string): readonly ProjectStatus[] {
  return isProjectStatus(status) ? STATUS_UEBERGAENGE[status] : [];
}
/**
 * Does the form still show the stored project, i.e. no unsaved input? The preselected
 * only company (F4) is no input of the person. `gespeichert` is the last server state of
 * the open project, not a row of the (filtered) list: a project that left the list after
 * a status change must stay changeable (review PR #63, finding 1).
 */
export function ohneEigeneEingaben(
  form: ProjektForm,
  gespeichert: Project | undefined,
  companies: readonly ProjectCompany[],
): boolean {
  if (gespeichert === undefined || form.id !== gespeichert.id) return false;
  const stand = formularAusProjekt(gespeichert);
  const eingabe = JSON.stringify(form);
  return (
    eingabe === JSON.stringify(stand) ||
    eingabe === JSON.stringify(mitVorbelegterGesellschaft(stand, companies))
  );
}
/** Does the list show this project with the current filter (AK 4)? */
export function inListe(status: ProjectStatus, alleZeigen: boolean): boolean {
  return alleZeigen || (ARBEITSLISTE_STATUSES as readonly ProjectStatus[]).includes(status);
}
/**
 * The list after the server returned project `p` (save, status change, reload): the
 * project is placed by number if the filter shows it and removed otherwise — also when
 * it returns to the working list, e.g. `verloren → akquise` (AC-202 walkthrough).
 */
export function listeMitProjekt(
  rows: readonly Project[],
  p: Project,
  alleZeigen: boolean,
): Project[] {
  return [
    ...rows.filter((row) => row.id !== p.id),
    ...(inListe(p.status, alleZeigen) ? [p] : []),
  ].sort((a, b) => a.number.localeCompare(b.number));
}
export function statusFehlermeldung(befund: FehlerBefund): string {
  if (befund.art === 'konflikt')
    return 'Das Projekt wurde zwischenzeitlich geändert. Der Status wurde nicht geändert. Bitte laden Sie den aktuellen Stand.';
  if (befund.art === 'eingabe')
    return 'Dieser Statuswechsel ist nicht erlaubt. Bitte laden Sie den aktuellen Stand.';
  const warning = befund.moeglicherweiseAusgefuehrt
    ? ' Der Status könnte bereits geändert sein. Bitte prüfen Sie den Stand vor einem weiteren Versuch.'
    : '';
  return befund.meldung + warning;
}

export interface ProjektFormularProps {
  form: ProjektForm;
  leaders: readonly ProjectLeader[];
  companies: readonly ProjectCompany[];
  lphGroups: readonly LphGroup[];
  busy: boolean;
  conflict: boolean;
  error: string | null;
  /** AC-202: may this person change the status (`darfStatusAendern`)? */
  darfStatus: boolean;
  /** AC-202: false while the form has unsaved input; the change would send a stale version. */
  statusAenderbar: boolean;
  readonly onStatusChange: (status: ProjectStatus) => void;
  readonly onChange: (form: ProjektForm) => void;
  readonly onSave: () => void;
  readonly onReload: () => void;
}
export function ProjektFormular({
  form,
  leaders,
  companies,
  lphGroups,
  busy,
  conflict,
  error,
  darfStatus,
  statusAenderbar,
  onStatusChange,
  onChange,
  onSave,
  onReload,
}: ProjektFormularProps): JSX.Element {
  const set = (key: keyof ProjektForm, value: string) => onChange({ ...form, [key]: value });
  const knownLeader = leaders.some((p) => p.id === form.projectManagerId);
  const ziele = statusZiele(form.status);
  const [ziel, setZiel] = useState<string>('');
  const passend = beauftragteLphGruppen(form.beauftragteLeistungsphasen, lphGroups) !== null;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <h3>{form.id === null ? 'Projekt anlegen' : 'Projekt bearbeiten'}</h3>
      <fieldset disabled={busy}>
        <label>
          Projektnummer{' '}
          <input required value={form.number} onChange={(e) => set('number', e.target.value)} />
        </label>
        <label>
          Name <input required value={form.name} onChange={(e) => set('name', e.target.value)} />
        </label>
        <label>
          Projektleitung{' '}
          <select
            required
            value={form.projectManagerId}
            onChange={(e) => set('projectManagerId', e.target.value)}
          >
            <option value="">Bitte wählen</option>
            {!knownLeader && form.projectManagerId !== '' && (
              <option value={form.projectManagerId}>
                Bisherige Projektleitung (nicht mehr aktiv)
              </option>
            )}
            {leaders.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        {form.id === null ? (
          <>
            <label>
              Status{' '}
              <select required value={form.status} onChange={(e) => set('status', e.target.value)}>
                {anlageStatuswerte(darfStatus).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_BEZEICHNUNG[s]}
                  </option>
                ))}
              </select>
            </label>
            {darfStatus && (
              <p>Ein abgeschlossenes Projekt lässt sich direkt als Referenz erfassen.</p>
            )}
          </>
        ) : (
          // AC-202 (E3): the status is changed by its own action, never by saving.
          <fieldset>
            <legend>Status: {statusBezeichnung(form.status)}</legend>
            {darfStatus && ziele.length > 0 && (
              <>
                <label>
                  Neuer Status{' '}
                  <select
                    value={ziel}
                    disabled={!statusAenderbar}
                    onChange={(e) => setZiel(e.target.value)}
                  >
                    <option value="">Bitte wählen</option>
                    {ziele.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_BEZEICHNUNG[s]}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={!statusAenderbar || !isProjectStatus(ziel)}
                  onClick={() => {
                    if (isProjectStatus(ziel)) onStatusChange(ziel);
                    setZiel('');
                  }}
                >
                  Status ändern
                </button>
                <p>
                  Der neue Status wirkt ab dieser Kalenderwoche auf die Auslastung; frühere Wochen
                  bleiben unverändert.
                  {!statusAenderbar && ' Bitte speichern oder verwerfen Sie zuerst Ihre Eingaben.'}
                </p>
              </>
            )}
          </fieldset>
        )}
        <label>
          Federführende Gesellschaft{' '}
          <select
            required
            value={form.companyId}
            onChange={(e) => set('companyId', e.target.value)}
          >
            <option value="">Bitte wählen</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend>Beauftragte Leistungsphasen, optional</legend>
          {lphGroups.map((gruppe) => (
            <label key={gruppe.label}>
              <input
                type="checkbox"
                checked={gruppe.phases.every((p) => form.beauftragteLeistungsphasen.includes(p))}
                onChange={(e) =>
                  onChange({
                    ...form,
                    beauftragteLeistungsphasen: schalteLphGruppe(
                      form.beauftragteLeistungsphasen,
                      lphGroups,
                      gruppe,
                      e.target.checked,
                    ),
                    phasenGeaendert: true,
                  })
                }
              />{' '}
              {gruppe.label}
            </label>
          ))}
          {!passend && (
            <p>
              Die gespeicherten Phasen ({lphAnzeige(form.beauftragteLeistungsphasen, lphGroups)})
              passen nicht zur aktuellen Gruppierung. Sie bleiben erhalten, solange Sie hier nichts
              ändern; mit der ersten Auswahl ersetzen die gewählten Gruppen sie.
            </p>
          )}
        </fieldset>
        <label>
          Honorar netto (€), optional{' '}
          <input
            inputMode="decimal"
            value={form.fee}
            onChange={(e) => set('fee', e.target.value)}
          />
        </label>
        <p>
          Ein leeres Honorar bedeutet „kein Honorar hinterlegt“. 0,00 € ist ein hinterlegtes
          Honorar.
        </p>
        {form.id !== null && <input type="hidden" name="version" value={form.version ?? ''} />}
        <button type="submit" disabled={conflict}>
          {busy ? 'Speichern …' : 'Speichern'}
        </button>
        {form.id !== null && (
          <button type="button" onClick={onReload}>
            Aktuellen Stand laden (Eingaben ersetzen)
          </button>
        )}
      </fieldset>
      {error !== null && <p role="alert">{error}</p>}
    </form>
  );
}

const DRAFT_KEY = 'ac201-projekt';
/**
 * AC-202 E5 / AC-401 E11: after closing or losing a project, say how many requested
 * and approved allocations remain from this week on. Nothing was deleted.
 */
export function kuenftigeBelegungenHinweis(anzahl: number | null): string | null {
  if (anzahl === null) return null;
  if (anzahl === 0) return 'Ab dieser Woche gibt es keine Belegungen auf diesem Projekt.';
  return `${String(anzahl)} ${anzahl === 1 ? 'Belegung bleibt' : 'Belegungen bleiben'} ab dieser Woche bestehen; gelöscht wurde nichts.`;
}

export function Projekte({
  darfSchreiben,
  darfStatus,
  darfArbeitspakete = false,
  darfAnfragen = false,
  inhaber,
}: {
  darfSchreiben: boolean;
  /** AC-202: `darfStatusAendern(rechte)`. */
  darfStatus: boolean;
  /** AC-303: `ansichtSichtbar('arbeitspakete', rechte)`. */
  darfArbeitspakete?: boolean;
  /** AC-404: `darfRessourcenAnfragen(rechte)`. */
  darfAnfragen?: boolean;
  inhaber: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadBlocked, setLoadBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState<readonly Project[]>([]);
  const [leaders, setLeaders] = useState<readonly ProjectLeader[]>([]);
  const [companies, setCompanies] = useState<readonly ProjectCompany[]>([]);
  const [lphGroups, setLphGroups] = useState<readonly LphGroup[]>([]);
  const [form, setForm] = useState<ProjektForm>(LEERES_PROJEKT);
  const [error, setError] = useState<string | null>(null);
  /** AC-401 E11: after `abgeschlossen`/`verloren`, how many allocations remain. */
  const [statusHinweis, setStatusHinweis] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [login, setLogin] = useState(false);
  const [restored, setRestored] = useState(false);
  /** AK 4: completed and lost projects are hidden unless asked for; the server filters. */
  const [alleZeigen, setAlleZeigen] = useState(false);
  /** AC-202: last server state of the open project, for `ohneEigeneEingaben`. */
  const [gespeichert, setGespeichert] = useState<Project | undefined>(undefined);
  /** AC-303: the project whose work packages are open; loading follows this click. */
  const [arbeitspaketeFuer, setArbeitspaketeFuer] = useState<Project | null>(null);
  const request = useRef(0);
  const saving = useRef(false);

  useEffect(() => {
    const saved = erzeugeEntwurfsspeicher({ inhaber }).holen(DRAFT_KEY, pruefeProjektEntwurf);
    if (saved !== null) {
      setForm(saved.eingabe);
      setRestored(true);
    }
    return () => {
      request.current += 1;
    };
  }, [inhaber]);

  async function load(alle = alleZeigen): Promise<void> {
    if (loadBlocked || saving.current) return;
    const token = ++request.current;
    setOpen(true);
    setLoading(true);
    setError(null);
    setStatusHinweis(null);
    try {
      const [rows, people, firmen, settings] = await Promise.all([
        trpc.projects.list.query(alle ? { statuses: [...PROJECT_STATUSES] } : undefined),
        trpc.projects.leaders.query(),
        trpc.projects.companies.query(),
        trpc.tenant.settings.query(),
      ]);
      if (request.current === token) {
        setProjects(rows);
        setLeaders(people);
        setCompanies(firmen);
        setLphGroups(settings.lphGroups);
        setForm((f) => (f.id === null ? mitVorbelegterGesellschaft(f, firmen) : f));
      }
    } catch (e) {
      if (request.current === token) {
        const problem = klassifiziereFehler(e, { schreibzugriff: false });
        setError(problem.meldung);
        setLogin(problem.neuAnmelden);
        if (problem.art === 'zugriff') setLoadBlocked(true);
      }
    } finally {
      if (request.current === token) setLoading(false);
    }
  }
  function edit(value: ProjektForm): void {
    // Auswahl, Eingabe, „Neues Projekt" und Neuladen laufen hier durch; die
    // Meldung zu künftigen Belegungen gilt dann nicht mehr (Review PR #67, Hinweis 4).
    setStatusHinweis(null);
    setForm(value);
    erzeugeEntwurfsspeicher({ inhaber }).merken(DRAFT_KEY, value);
  }
  async function reload(): Promise<void> {
    if (form.id === null || saving.current || loadBlocked) return;
    const token = ++request.current;
    setLoading(true);
    try {
      const p = await trpc.projects.get.query({ id: form.id });
      if (request.current === token) {
        edit(formularAusProjekt(p));
        setGespeichert(p);
        setProjects((rows) => listeMitProjekt(rows, p, alleZeigen));
        setConflict(false);
        setError(null);
      }
    } catch (e) {
      if (request.current === token) {
        const problem = klassifiziereFehler(e, { schreibzugriff: false });
        setError(problem.meldung);
        setLogin(problem.neuAnmelden);
        if (problem.art === 'zugriff') setLoadBlocked(true);
      }
    } finally {
      if (request.current === token) setLoading(false);
    }
  }
  async function save(): Promise<void> {
    if (saving.current || loading || conflict || loadBlocked) return;
    let input: ReturnType<typeof projektEingabe>;
    try {
      input = projektEingabe(form);
    } catch {
      setError('Bitte prüfen Sie Pflichtfelder und Honorar (höchstens zwei Nachkommastellen).');
      return;
    }
    saving.current = true;
    setBusy(true);
    setError(null);
    setStatusHinweis(null);
    setLogin(false);
    request.current += 1;
    const token = request.current;
    try {
      const p = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('project', form.id ?? 'neu'),
        async () => {
          if ('id' in input) return await trpc.projects.update.mutate(input);
          return await trpc.projects.create.mutate(input);
        },
      );
      if (request.current === token) {
        // AK 4: a project created as completed is not part of the working list.
        setProjects((rows) => listeMitProjekt(rows, p, alleZeigen));
        setForm(formularAusProjekt(p));
        setGespeichert(p);
        setRestored(false);
        erzeugeEntwurfsspeicher({ inhaber }).vergessen(DRAFT_KEY);
      }
    } catch (e) {
      if (request.current === token) {
        const problem = klassifiziereFehler(e, { schreibzugriff: true });
        setError(projektFehlermeldung(problem, form.id !== null));
        setConflict(problem.art === 'konflikt' && form.id !== null);
        setLogin(problem.neuAnmelden);
        erzeugeEntwurfsspeicher({ inhaber }).merken(DRAFT_KEY, form);
      }
    } finally {
      saving.current = false;
      if (request.current === token) setBusy(false);
    }
  }
  async function changeStatus(status: ProjectStatus): Promise<void> {
    const { id, version } = form;
    if (id === null || version === null || saving.current || loading || loadBlocked) return;
    saving.current = true;
    setBusy(true);
    setError(null);
    setStatusHinweis(null);
    setLogin(false);
    request.current += 1;
    const token = request.current;
    try {
      const { kuenftigeBelegungen, ...p } = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('project', id),
        () => trpc.projects.changeStatus.mutate({ id, version, status }),
      );
      if (request.current === token) {
        setStatusHinweis(kuenftigeBelegungenHinweis(kuenftigeBelegungen));
        setProjects((rows) => listeMitProjekt(rows, p, alleZeigen));
        setForm(formularAusProjekt(p));
        setGespeichert(p);
      }
    } catch (e) {
      if (request.current === token) {
        const problem = klassifiziereFehler(e, { schreibzugriff: true });
        setError(statusFehlermeldung(problem));
        setLogin(problem.neuAnmelden);
      }
    } finally {
      saving.current = false;
      if (request.current === token) setBusy(false);
    }
  }
  if (!open)
    return (
      <section>
        <h2>Projekte</h2>
        <button
          onClick={() => {
            void load();
          }}
        >
          Projekte öffnen
        </button>
      </section>
    );
  return (
    <section aria-label="Projekte">
      <h2>Projekte</h2>
      <button
        disabled={busy || loading || loadBlocked}
        onClick={() => {
          void load();
        }}
      >
        Projektliste neu laden
      </button>
      <label>
        <input
          type="checkbox"
          checked={alleZeigen}
          disabled={busy || loading || loadBlocked}
          onChange={(e) => {
            setAlleZeigen(e.target.checked);
            void load(e.target.checked);
          }}
        />{' '}
        Abgeschlossene, verlorene und archivierte Projekte einblenden
      </label>
      <div role="status" aria-live="polite">
        {loading && <p>Projekte werden geladen …</p>}
        {restored && (
          <p>Ihr Entwurf wurde wiederhergestellt. Bitte prüfen und bewusst speichern.</p>
        )}
        {statusHinweis !== null && <p>{statusHinweis}</p>}
      </div>
      {error !== null && <p role="alert">{error}</p>}
      {projects.length === 0 && !loading && <p>Keine Projekte vorhanden.</p>}
      <table>
        <thead>
          <tr>
            <th>Nummer</th>
            <th>Name</th>
            <th>Projektleitung</th>
            <th>Gesellschaft</th>
            <th>Status</th>
            <th>Beauftragte LPH</th>
            <th>Herkunft</th>
            <th>Honorar netto</th>
            {darfSchreiben && <th>Aktion</th>}
            {darfArbeitspakete && <th>Arbeitspakete</th>}
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id}>
              <td>{p.number}</td>
              <td>{p.name}</td>
              <td>
                {leaders.find((l) => l.id === p.projectManagerId)?.name ??
                  'Bisherige Projektleitung'}
              </td>
              <td>
                {p.companyId === null
                  ? 'nicht gewählt'
                  : (companies.find((c) => c.id === p.companyId)?.name ?? '')}
              </td>
              <td>{statusBezeichnung(p.status)}</td>
              <td>{lphAnzeige(p.beauftragteLeistungsphasen, lphGroups)}</td>
              <td>{p.herkunft}</td>
              <td>{p.fee === null ? 'Kein Honorar hinterlegt' : `${p.fee.replace('.', ',')} €`}</td>
              {darfSchreiben && (
                <td>
                  <button
                    disabled={busy || loading || loadBlocked}
                    onClick={() => {
                      edit(mitVorbelegterGesellschaft(formularAusProjekt(p), companies));
                      setGespeichert(p);
                      setConflict(false);
                      setError(null);
                    }}
                  >
                    Bearbeiten
                  </button>
                </td>
              )}
              {darfArbeitspakete && (
                <td>
                  <button
                    disabled={busy || loading}
                    onClick={() => {
                      setArbeitspaketeFuer(p);
                    }}
                  >
                    Arbeitspakete
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {darfArbeitspakete && arbeitspaketeFuer !== null && (
        <ArbeitspaketeBereich
          key={arbeitspaketeFuer.id}
          projekt={arbeitspaketeFuer}
          darfSchreiben={darfSchreiben}
          darfStatus={darfStatus}
          darfAnfragen={darfAnfragen}
          onSchliessen={() => {
            setArbeitspaketeFuer(null);
          }}
        />
      )}
      {darfSchreiben && (
        <>
          <button
            disabled={busy || loading || loadBlocked}
            onClick={() => {
              edit(mitVorbelegterGesellschaft(LEERES_PROJEKT, companies));
              setGespeichert(undefined);
              setConflict(false);
              setError(null);
            }}
          >
            Neues Projekt
          </button>
          <ProjektFormular
            // A different project gets a fresh status choice (review PR #63, finding 2).
            key={form.id ?? 'neu'}
            form={form}
            leaders={leaders}
            companies={companies}
            lphGroups={lphGroups}
            busy={busy || loading || loadBlocked}
            conflict={conflict}
            error={null}
            darfStatus={darfStatus}
            statusAenderbar={ohneEigeneEingaben(form, gespeichert, companies)}
            onStatusChange={(status) => {
              void changeStatus(status);
            }}
            onChange={edit}
            onSave={() => {
              void save();
            }}
            onReload={() => {
              void reload();
            }}
          />
        </>
      )}
      {login && (
        <a href={anmeldeUrlMitRueckkehr('/')}>Erneut anmelden (Eingaben bleiben erhalten)</a>
      )}
    </section>
  );
}
